import {
  CompanyStatus,
  CompanyTypes,
  CompanyVerificationStatus,
} from "@shared_packages/toolkit/types";
import { type FC } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  Button,
  ButtonVariant,
  Status,
  TooltipText,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import { countries } from "../../../../constants/countries";
import { AppRoutes } from "../../../../constants/routes";
import { useModalManager } from "../../../../context/ModalManager";
import { FullCompanyApiType } from "../../../../util/types";
import {
  CardEditButton,
  DetailCard,
  DetailCardRow,
  DetailCardsGrid,
  DetailCardStatusRow,
  externalHrefLabel,
} from "../../components/detail-cards";
import {
  EditCompanyCoreDetailsModal,
  type EditCompanyDetailsSection,
} from "./EditCompanyCoreDetailsModal";
import SubscriptionSection from "./SubscriptionSection";

interface CompanyInfoProps {
  /** May be undefined on first paint before `getCompany` resolves. */
  data: FullCompanyApiType | undefined;
  onCompanySaved: (company: FullCompanyApiType) => void;
  onVerifyClick: () => Promise<void>;
  onVerificationRejectClick: () => Promise<void>;
  onActivateClick: () => Promise<void>;
  onBanClick: () => Promise<void>;
  onRefresh: () => void;
  partnersErrors?: string[];
  onMarkAsPartnerClick?: () => void;
}

const ActionButton = styled(Button)`
  flex-shrink: 0;
  white-space: nowrap;
  padding: 6px 14px;
  min-height: 30px;
  height: auto;
  width: auto;
  min-width: unset;
`;

const CompanyInfo: FC<CompanyInfoProps> = ({
  data,
  onCompanySaved,
  onActivateClick,
  onBanClick,
  onVerificationRejectClick,
  onVerifyClick,
  onRefresh,
  partnersErrors = [],
  onMarkAsPartnerClick,
}) => {
  const { addModal } = useModalManager();

  if (!data) {
    return null;
  }

  const mcc = data.mcc;

  const hideTradingAddress =
    data.type === CompanyTypes.CONSUMER ||
    data.type === CompanyTypes.SOLE_TRADER;

  const openCompanyEdit = (section: EditCompanyDetailsSection) => {
    addModal(
      <EditCompanyCoreDetailsModal
        section={section}
        data={{
          _id: data._id,
          name: data.name,
          tradingName: data.tradingName,
          countryCode: data.countryCode,
          alias: data.alias,
          industry: data.industry,
          taxNumber: data.taxNumber,
          publicEmail: data.publicEmail,
          supportPhone: data.supportPhone,
          phoneNumber: data.phoneNumber,
          phoneCode: data.phoneCode,
          phoneCountryCode: data.phoneCountryCode,
          publicWebsite: data.publicWebsite,
          privacyPolicy: data.privacyPolicy,
          termsOfService: data.termsOfService,
          address: data.address,
          tradingAddress: data.tradingAddress,
        }}
        onSuccess={async (updated) => {
          if (updated) {
            onCompanySaved(updated);
          } else {
            await Promise.resolve(onRefresh());
          }
        }}
      />
    );
  };

  const registeredCountryName = countries.find(
    (c) => c.country === data?.address?.country
  )?.countryName;

  const ownerName = [data?.createdBy?.firstName, data?.createdBy?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const ownerDisplay =
    ownerName || data?.createdBy?.email || data?.createdBy?._id || undefined;

  const userDetailsPath = data?.createdBy?._id
    ? `${AppRoutes.Private.Users.DETAILS}${data.createdBy._id}`
    : "";

  const ownerValue =
    data?.createdBy?._id != null ? (
      <Link to={userDetailsPath} style={{ color: "#027aff" }}>
        {ownerDisplay ?? data.createdBy._id}
      </Link>
    ) : (
      ownerDisplay || data?.createdBy?.email || undefined
    );
  const supportPhoneDisplay =
    data?.phoneCode && data?.phoneNumber
      ? `${data.phoneCode} ${data.phoneNumber}`
      : data?.supportPhone;

  return (
    <>
      <DetailCardsGrid>
        <DetailCard
          title="Company overview"
          headerAction={
            <CardEditButton onClick={() => openCompanyEdit("overview")}>
              Edit
            </CardEditButton>
          }
        >
          <DetailCardRow label="Fena ID" value={data?._id} />
          <DetailCardRow label="Company name" value={data?.name} />
          <DetailCardRow label="Trading name" value={data?.tradingName} />
          <DetailCardRow label="Display name" value={data?.alias} />
          <DetailCardRow label="Country code" value={data?.countryCode} />
          <DetailCardRow label="Industry" value={data?.industry} />
          <DetailCardRow label="Owner" value={ownerValue} />
        </DetailCard>

        <DetailCard title="Status">
          <DetailCardStatusRow
            label="Account status"
            status={<Status status={data?.status} />}
            actions={
              <>
                <ActionButton
                  onClick={onActivateClick}
                  disabled={data?.status === CompanyStatus.ACTIVE}
                >
                  Activate
                </ActionButton>
                <ActionButton
                  onClick={onBanClick}
                  variant={ButtonVariant.OUTLINED}
                  disabled={data?.status === CompanyStatus.BANNED}
                >
                  Ban
                </ActionButton>
              </>
            }
          />
          <DetailCardStatusRow
            label="Verification status"
            status={<Status status={data?.verificationStatus} />}
            actions={
              <>
                <ActionButton
                  onClick={onVerifyClick}
                  disabled={
                    data?.verificationStatus ===
                    CompanyVerificationStatus.VERIFIED
                  }
                >
                  Verify
                </ActionButton>
                <ActionButton
                  onClick={onVerificationRejectClick}
                  variant={ButtonVariant.OUTLINED}
                  disabled={
                    data?.verificationStatus ===
                    CompanyVerificationStatus.FINAL_REJECTED
                  }
                >
                  Reject
                </ActionButton>
              </>
            }
          />
          <DetailCardStatusRow
            label="Direct debit consent"
            status={
              <>
                <Status
                  status={
                    data?.directDebitConsentEnabled ? "enabled" : "disabled"
                  }
                />
                {data?.directDebitConsentEnabled &&
                  data?.directDebitConsentDate && (
                    <Typography
                      variant={TypographyVariant.BODY2}
                      style={{ color: "#666", whiteSpace: "nowrap" }}
                    >
                      Since:{" "}
                      {new Date(
                        data.directDebitConsentDate
                      ).toLocaleDateString()}
                    </Typography>
                  )}
              </>
            }
          />
          <DetailCardStatusRow
            label="Partner access"
            status={
              <Status
                status={data?.canManageCompanies ? "enabled" : "disabled"}
              />
            }
            actions={
              !data?.canManageCompanies && onMarkAsPartnerClick ? (
                <TooltipText
                  id="mark-as-partner-tooltip-status-card"
                  text={
                    partnersErrors.length === 0
                      ? "Company is ready to be marked as a partner"
                      : partnersErrors.join(", ")
                  }
                >
                  <span>
                    <ActionButton
                      onClick={onMarkAsPartnerClick}
                      variant={ButtonVariant.OUTLINED}
                      disabled={partnersErrors.length > 0}
                    >
                      Mark as partner
                    </ActionButton>
                  </span>
                </TooltipText>
              ) : undefined
            }
          />
        </DetailCard>

        <DetailCard
          title="Registered address"
          headerAction={
            <CardEditButton onClick={() => openCompanyEdit("registered")}>
              Edit
            </CardEditButton>
          }
        >
          <DetailCardRow
            label="Address line 1"
            value={data?.address?.addressLine1}
          />
          <DetailCardRow
            label="Address line 2"
            value={data?.address?.addressLine2}
          />
          <DetailCardRow label="City" value={data?.address?.city} />
          <DetailCardRow label="Country" value={registeredCountryName} />
          <DetailCardRow label="Postcode" value={data?.address?.zipCode} />
        </DetailCard>

        {!hideTradingAddress && (
          <DetailCard
            title="Trading address"
            headerAction={
              <CardEditButton onClick={() => openCompanyEdit("trading")}>
                Edit
              </CardEditButton>
            }
          >
            <DetailCardRow
              label="Address line 1"
              value={data?.tradingAddress?.addressLine1}
            />
            <DetailCardRow
              label="Address line 2"
              value={data?.tradingAddress?.addressLine2}
            />
            <DetailCardRow label="City" value={data?.tradingAddress?.city} />
            <DetailCardRow
              label="Country"
              value={
                countries.find(
                  (c) => c.country === data?.tradingAddress?.country
                )?.countryName
              }
            />
            <DetailCardRow
              label="Postcode"
              value={data?.tradingAddress?.zipCode}
            />
          </DetailCard>
        )}

        <DetailCard
          title="Legal"
          headerAction={
            <CardEditButton onClick={() => openCompanyEdit("legal")}>
              Edit
            </CardEditButton>
          }
        >
          <DetailCardRow label="Type" value={data?.type} />
          <DetailCardRow
            label="Company registration number (CRN)"
            value={data?.identifier}
          />
          <DetailCardRow label="Tax number" value={data?.taxNumber} />
          <DetailCardRow label="MCC" value={mcc ?? undefined} />
        </DetailCard>

        <DetailCard
          title="Contact"
          headerAction={
            <CardEditButton onClick={() => openCompanyEdit("contact")}>
              Edit
            </CardEditButton>
          }
        >
          <DetailCardRow label="Public email" value={data?.publicEmail} />
          <DetailCardRow label="Support phone" value={supportPhoneDisplay} />
          <DetailCardRow
            label="Public website"
            value={externalHrefLabel(data?.publicWebsite)}
          />
          <DetailCardRow
            label="Privacy policy"
            value={externalHrefLabel(data?.privacyPolicy)}
          />
          <DetailCardRow
            label="Terms of service"
            value={externalHrefLabel(data?.termsOfService)}
          />
        </DetailCard>
      </DetailCardsGrid>

      {/* <SubscriptionSection
        data={data}
        onRefresh={onRefresh}
        onEnableStripePaymentClick={onEnableStripePaymentClick}
        onDisableStripePaymentClick={onDisableStripePaymentClick}
      /> */}
    </>
  );
};

export default CompanyInfo;
